import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { updateGroup, findGroupsByCartId } from "../lib/cartGroupApi";
import { setCart } from "../redux/cartReducer";
import { DateService } from "../utils/dateUtils";
import { registerDateUpdate } from "../redux/cartOperationsAdapter";

/**
 * A hook to handle delivery date updates
 * Uses CartOperationManager for centralized operation management
 */
const useDeliveryDateUpdate = () => {
  const dispatch = useDispatch();

  const updateDeliveryDate = useCallback(
    async (groupId, dateString) => {
      if (!groupId) {
        console.error("Missing groupId for delivery date update");
        return false;
      }

      console.log(
        `[DEBUG-UPDATE] updateDeliveryDate called for group ${groupId}`,
        {
          dateString,
          timestamp: new Date().toISOString(),
        }
      );

      const normalizedDate = dateString
        ? DateService.convertToApiFormat(dateString)
        : null;

      // We'll now accept null/undefined dates since this is an expected backend case
      if (dateString && !normalizedDate) {
        console.error("Invalid date format received:", dateString);
        return false;
      }

      // Register operation through the adapter - this will handle setting loading states
      const operation = registerDateUpdate(dispatch, groupId, normalizedDate);

      // Make the API call and wait for response - FIX: actually await the API call
      try {
        // Update the group
        console.log(`[DEBUG-UPDATE] Making API call for group ${groupId}`);
        const response = await updateGroup(groupId, {
          deliveryDate: normalizedDate,
          recalculateFee: false,
          recalculateWeight: false,
        });

        console.log(
          `[DEBUG-UPDATE] API response received for group ${groupId}:`,
          {
            success: !!response,
            timestamp: new Date().toISOString(),
          }
        );

        // Fetch updated cart
        const cartId = localStorage.getItem("cartId");
        if (cartId) {
          console.log(
            `[DEBUG-UPDATE] Fetching updated cart for group ${groupId}`
          );
          const response = await findGroupsByCartId(cartId);
          if (response?.cart) {
            dispatch(setCart(response.cart));

            console.log(
              `[DEBUG-UPDATE] Cart updated successfully for group ${groupId}`,
              {
                cartVersion: response.cart.cartVersion,
                timestamp: new Date().toISOString(),
              }
            );
          }
        }

        // Mark operation as complete AFTER API calls complete
        operation.complete(true);
        console.log(
          `[DEBUG-UPDATE] Operation completed successfully for group ${groupId}`,
          {
            timestamp: new Date().toISOString(),
          }
        );

        return true;
      } catch (error) {
        console.error(
          `[DEBUG-UPDATE] Error updating delivery date for group ${groupId}:`,
          error
        );
        operation.complete(false);
        return false;
      }
    },
    [dispatch]
  );

  return { updateDeliveryDate };
};

export default useDeliveryDateUpdate;
